import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Users, Home, Briefcase, Heart, ArrowRight, type LucideIcon } from 'lucide-react';
import { ProviderOverviewSkeleton } from '@/features/resource-hub/components/ProviderOverviewSkeleton';
import { JourneyProgress } from './JourneyProgress';
import { apiClient } from '@/core/api/client';

const ICON_CYCLE: LucideIcon[] = [Users, Home, Briefcase, Heart];

const getOptionIcon = (name: string, index: number): LucideIcon => {
  const lower = name.toLowerCase();
  if (lower.includes('home') || lower.includes('caregiver') || lower.includes('in-home')) return Home;
  if (lower.includes('law') || lower.includes('attorney') || lower.includes('legal') || lower.includes('financial')) return Briefcase;
  if (lower.includes('support') || lower.includes('health') || lower.includes('wellness') || lower.includes('hospice')) return Heart;
  if (lower.includes('manager') || lower.includes('coordinator') || lower.includes('specialist') || lower.includes('aging life')) return Users;
  return ICON_CYCLE[index % ICON_CYCLE.length];
};

const getOptionTag = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.includes('memory') || lower.includes('cognitive') || lower.includes('dementia')) return 'Cognitive Health';
  if (lower.includes('support') || lower.includes('caregiver')) return 'Caregiver Support';
  if (lower.includes('home') || lower.includes('in-home')) return 'Home Care';
  if (lower.includes('manager') || lower.includes('coordinator') || lower.includes('aging life')) return 'Care Coordination';
  if (lower.includes('law') || lower.includes('attorney') || lower.includes('legal') || lower.includes('financial')) return 'Legal & Financial';
  if (lower.includes('health') || lower.includes('medical') || lower.includes('nursing')) return 'Health & Medical';
  return 'Care Support';
};

const STICKY_OFFSET = 160;

interface ProviderOverviewState {
  sessionId?: string;
  carePlanId?: string;
}

type CareOption = {
  id: string;
  category_id: string;
  name: string;
  what_they_do: string;
  why_you_need_them: string;
};

export function ProviderOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ProviderOverviewState | null;
  const sessionId = state?.sessionId;
  const carePlanId = state?.carePlanId;

  const { data: careOptions, isLoading, isError, error } = useQuery({
    queryKey: ['careOptions', sessionId],
    queryFn: async () => {
      const response = await apiClient.get<CareOption[]>('/care-options', {
        params: { session_id: sessionId },
      });
      return response.data;
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000,
  });

  const options = careOptions ?? [];

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!options.length) return;
    const handleScroll = () => {
      const y = window.scrollY + STICKY_OFFSET + 20;
      let current = 0;
      options.forEach((option, i) => {
        const el = document.getElementById(`provider-card-${option.id}`);
        if (el && el.offsetTop <= y) current = i;
      });
      setActiveIndex(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [options]);

  const scrollToCard = (id: string) => {
    const el = document.getElementById(`provider-card-${id}`);
    if (!el) return;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET + 12,
      behavior: 'smooth',
    });
  };

  const goToMatching = () => {
    navigate('/redesign-chatbot/matching/0', { state: { sessionId, carePlanId } });
  };

  return (
    <div className="min-h-screen bg-background">
      <JourneyProgress currentStep="overview" sessionId={sessionId} carePlanId={carePlanId} />

      {/* ── Page body ──────────────────────────────────────────────────────── */}
      <div className="px-3 py-3 ">
        <div className="">

          {/* ── ONE large white container card — NO overflow-hidden so sticky works ── */}
          <div className="flex min-h-[600px] items-start rounded-2xl border border-gray-200 bg-white shadow-sm">

            {/* ── LEFT SIDEBAR ────────────────────────────────────────────── */}
            <aside
              className="hidden w-[280px] flex-shrink-0 self-start rounded-l-2xl border-r border-gray-100 bg-white lg:flex lg:flex-col"
              style={{ position: 'sticky', top: STICKY_OFFSET, maxHeight: `calc(100vh - ${STICKY_OFFSET}px)` }}
            >
              {/* Sidebar header */}
              <div className="border-b border-gray-100 px-5 py-5">
                <h2 className="text-sm font-semibold text-gray-900">Provider Types</h2>
              </div>

              {/* Nav items — scrollable if many */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <nav className="space-y-0.5">
                  {options.map((option, i) => {
                    const isActive = i === activeIndex;
                    const isCompleted = i < activeIndex;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => scrollToCard(option.id)}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          isActive
                            ? 'bg-[#026857]/5 font-medium text-[#026857]'
                            : isCompleted
                              ? 'text-[#026857] hover:bg-[#026857]/5'
                              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                        }`}
                      >
                        <span
                          className={`relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                            isCompleted
                              ? 'border-[#026857] bg-[#026857]'
                              : isActive
                                ? 'border-[#026857] bg-white shadow-[0_0_0_3px_rgba(2,104,87,0.15)]'
                                : 'border-gray-300 bg-white'
                          }`}
                        >
                          {isActive && <span className="h-1.5 w-1.5 rounded-full bg-[#026857]" />}
                        </span>
                        <span className="leading-snug">{option.name}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Start Matching button */}
              <div className="border-t border-gray-100 px-4 py-4">
                <Button
                  onClick={goToMatching}
                  size="sm"
                  variant="outline"
                  className="w-full border-[#026857] bg-white text-[#026857] transition-colors hover:bg-[#026857]/5 hover:text-[#026857]"
                >
                  Start Matching
                </Button>
              </div>
            </aside>

            {/* ── RIGHT CONTENT ───────────────────────────────────────────── */}
            <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">

              {/* Header section */}
              <div className="mb-6 border-b border-gray-100 pb-6 sm:mb-8 sm:pb-8">
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[#026857]">
                  Step 3 of 5 · Your Care Team Overview
                </p>
                <h1 className="mb-2 text-2xl font-semibold text-gray-900 sm:text-3xl">Your Care Team Overview</h1>
                <p className="leading-relaxed text-gray-600">
                  Based on your situation, you'll need to work with several types of service providers.
                  Here's what each one does and why they're important.
                </p>

              </div>

              {/* Provider timeline */}
              {isLoading && <ProviderOverviewSkeleton />}

              {isError && (
                <div className="py-12 text-center text-gray-500">
                  {(error as Error)?.message || 'Unable to load care options. Please try again.'}
                </div>
              )}

              {!isLoading && !isError && (
                <div className="relative flex flex-col">
                  {/* Continuous timeline track */}
                  {options.length > 1 && (
                    <div
                      className="absolute left-[0.875rem] top-[0.875rem] bottom-[0.875rem] w-0.5 overflow-hidden rounded-full bg-gray-200"
                      aria-hidden="true"
                    >
                      <div
                        className="w-full rounded-full bg-[#026857] transition-[height] duration-500 ease-out"
                        style={{ height: `${(activeIndex / (options.length - 1)) * 100}%` }}
                      />
                    </div>
                  )}

                  {options.map((option, index) => {
                    const Icon = getOptionIcon(option.name, index);
                    const isLast = index === options.length - 1;
                    const isActive = index === activeIndex;

                    return (
                      <div
                        key={option.id}
                        id={`provider-card-${option.id}`}
                        className={`flex gap-3 sm:gap-5 ${isLast ? '' : 'pb-8'}`}
                      >
                        {/* Milestone dot */}
                        <div className="relative z-[1] flex flex-shrink-0 flex-col items-center">
                          <div
                            className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-bold transition-all duration-300 ${
                              isActive
                                ? 'scale-[1.15] border-[#026857] bg-[#026857] text-white shadow-[0_0_10px_rgba(2,104,87,0.35)]'
                                : index < activeIndex
                                  ? 'border-[#026857] bg-[#026857] text-white'
                                  : 'border-gray-200 bg-white text-gray-500'
                            }`}
                          >
                            {index + 1}
                          </div>
                        </div>

                        {/* Provider card:
                              active   → teal border, highlighted bg, full opacity
                              passed   → normal white, full opacity (already reviewed)
                              upcoming → dimmed, gray, faded */}
                        <div
                          className={`min-w-0 flex-1 rounded-xl border p-4 transition-all duration-300 sm:p-6 ${
                            isActive
                              ? 'scale-[1.005] border-[#026857] bg-[#fbfcfa] shadow-[0_8px_24px_rgba(2,104,87,0.08)]'
                              : index < activeIndex
                                ? 'border-gray-200 bg-white opacity-80'
                                : 'scale-[0.98] border-gray-100 bg-gray-50/50 opacity-50'
                          }`}
                        >
                          {/* Identity + info panels: stacked on mobile, two-column from sm up */}
                          <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">

                            {/* LEFT: icon → name → tag (stacked) */}
                            <div className="flex-shrink-0 sm:w-[180px]">
                              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#026857] to-[#02584a]">
                                <Icon className="text-white" size={20} />
                              </div>
                              <h2 className="mb-2 text-base font-semibold leading-snug text-gray-900">{option.name}</h2>
                              <span className="inline-flex items-center rounded-full bg-[#026857]/10 px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide text-[#026857]">
                                {getOptionTag(option.name)}
                              </span>
                            </div>

                            {/* RIGHT: What They Do + Why You Need Them stacked */}
                            <div className="flex min-w-0 flex-1 flex-col gap-3">
                              {/* What They Do */}
                              <div className="rounded-lg border border-gray-100 bg-white px-4 py-3">
                                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                  What They Do
                                </p>
                                <p className="text-sm leading-relaxed text-gray-600">{option.what_they_do}</p>
                              </div>

                              {/* Why You Need Them */}
                              <div className="rounded-lg border-l-4 border-[#026857] bg-[#026857]/5 px-4 py-3">
                                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#026857]">
                                  Why You Need Them
                                </p>
                                <p className="text-sm leading-relaxed text-gray-700">{option.why_you_need_them}</p>
                              </div>
                            </div>

                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* CTA Section */}
              <div
                id="provider-card-cta"
                className="mt-8 rounded-xl border border-[#026857]/20 bg-gradient-to-br from-[#026857]/5 to-[#026857]/10 p-4 text-center sm:mt-10 sm:p-8"
              >
                <h2 className="mb-3 text-xl font-semibold text-gray-900 sm:text-2xl">Ready to Find Your Providers?</h2>
                <p className="mx-auto mb-6 max-w-2xl leading-relaxed text-gray-700">
                  Now that you understand each role, we'll help you find the right professionals
                  for your specific situation. We'll go through each provider type one at a time.
                </p>

                <div className="mb-6 flex flex-wrap items-center justify-center gap-3 text-sm text-gray-600">
                  <span>{options.length || 4} provider types</span>
                  <span>•</span>
                  <span>~5 minutes</span>
                  <span>•</span>
                  <span>Personalized recommendations</span>
                </div>

                <Button
                  onClick={goToMatching}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#026857] px-6 py-5 text-base text-white hover:bg-[#02584a] sm:w-auto sm:px-8 sm:py-6 sm:text-lg"
                  size="lg"
                >
                  Start Matching Providers
                  <ArrowRight size={20} />
                </Button>
              </div>
            </main>

          </div>
        </div>
      </div>
    </div>
  );
}
